import React, { useState, useEffect, useCallback, useRef } from "react";
// 問い合わせ(チャット)画面のスタイル。以前は MenuPage 経由で読み込まれていたが、
// マイページアプリ管理の削除に伴い、当ページで明示的に読み込む。
import "../../../styles/mypage.css";
import {
  Loader,
  Loader2,
  X,
  Image,
  Send,
  Video,
  Paperclip,
  ChevronLeft,
  User,
  MoreVertical,
} from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { slashAppChat } from "../../../utils/slashAppChat";
import {
  ensureSlashAppTokens,
  SLASHAPP_AUTH_LOST_EVENT,
} from "../../../utils/slashAppClient";
import { useAuth } from "../../../contexts/AuthContext";

/**
 * ChatPage — slash-app の /api/v1/chat/ + WebSocket を呼ぶ。
 *
 * 旧 admin 自前の /api/mypage/get_chat_users/ などを叩く実装からの差し替え
 * (Phase 7)。データ移行は行っておらず新システムから開始する前提なので、
 * 旧フィールド (is_from_admin, delete_flg, chat_user.id 等) との互換性は持たず
 * 新仕様 (sender_sub_type, deleted, thread.id) にネイティブに合わせている。
 *
 * 認証: admin ログイン時に裏で slash-app の JWT を取得して localStorage に
 * 保持しているので、slashAppChat 経由の fetch / WebSocket がそれを使う。
 */

const ScrollLoader = () => (
  <div className="scroll-loader">
    <Loader2 className="animate-spin" color="#157edf" size={35} />
  </div>
);

const isFromInstructor = (m) => m?.sender_sub_type === "instructor";
const isLineThread = (t) => t?.inquiry_source === "line";

// LINE プロフィール画像。URL 未設定 or 取得失敗時は人型アイコンにフォールバック。
const LineAvatar = ({ src, alt }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className="user-avatar user-avatar-default">
        <User size={22} />
      </div>
    );
  }
  return (
    <img
      className="user-avatar user-avatar-img"
      src={src}
      alt={alt}
      onError={() => setFailed(true)}
    />
  );
};

const SourceBadge = ({ thread }) => {
  const line = isLineThread(thread);
  return (
    <span className={`source-badge ${line ? "line" : "mypage"}`}>
      {line ? "LINE" : "アプリ"}
    </span>
  );
};

/**
 * 家族アカウント (兄弟姉妹) の併記。
 *
 * マイページのアカウントはメールアドレス単位で、兄弟姉妹は 1 アカウントを
 * 共有する。表示名は代表 1 人 (在籍者を優先) しか出ないため、「誰の件の
 * 問い合わせか」で混乱しやすい。そこで代表以外の家族を小さく併記する。
 * 1 人だけの家族 (=大多数) では何も出さない。
 */
const FamilyNote = ({ customer }) => {
  const family = customer?.family;
  if (!Array.isArray(family) || family.length < 2) return null;
  const others = family.slice(1);
  return (
    <span className="chat-family-note">
      家族:{" "}
      {others
        .map((m) => `${m.full_name}${m.is_withdrawn ? "（退会）" : ""}`)
        .join("・")}
    </span>
  );
};

/**
 * LINE への配信結果。LINE スレッドへこちらから送ったメッセージにだけ出す。
 *
 * DB 保存と LINE への送信は別物で、送信だけ失敗することがある (月間配信上限
 * 超過など)。以前はそれが画面に出ず「送信済み」に見えたままだったので、
 * 失敗をはっきり出して再送できるようにしている。
 *
 * 成功時は何も出さない (LINE 公式アカウントと同じで、届いたことは無表示が既定)。
 * ステータスを持たない過去のメッセージ ("") も同様に無表示。
 */
const LineDeliveryNotice = ({ message, onResend, resending }) => {
  const status = message?.line_delivery_status;
  if (status === "pending") {
    return <div className="line-delivery pending">LINEへ送信中…</div>;
  }
  if (status !== "failed") return null;
  return (
    <div className="line-delivery failed">
      <span className="line-delivery-text">
        LINEへの送信に失敗しました
        {message.line_delivery_error ? `（${message.line_delivery_error}）` : ""}
      </span>
      {/* onResend 未指定 = 読み取り専用 (プレビューモーダル) */}
      {onResend && (
        <button
          type="button"
          className="line-resend-button"
          onClick={() => onResend(message.id)}
          disabled={resending}
        >
          {resending ? "再送中…" : "再送"}
        </button>
      )}
    </div>
  );
};

function ChatPage() {
  const [threads, setThreads] = useState([]);
  const [thread, setThread] = useState(null); // 現在開いているスレッド
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");

  const [threadPage, setThreadPage] = useState(1);
  const [hasMoreThreads, setHasMoreThreads] = useState(true);

  const [oldestId, setOldestId] = useState(null);
  const [hasMoreMessages, setHasMoreMessages] = useState(true);

  const [isLoadingThreads, setIsLoadingThreads] = useState(false);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const [filter, setFilter] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [incomingMessage, setIncomingMessage] = useState(null);

  const [expandedMedia, setExpandedMedia] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);

  // LINE 再送中の message id。連打とタブ間の二重送信はサーバ側でも弾くが、
  // ボタンを無効化して「押しても何も起きない」ように見えるのを防ぐ。
  const [resendingIds, setResendingIds] = useState([]);

  // チャット認証が回復不能 (セッション失効など) の時に再ログイン導線を出す
  const [authLost, setAuthLost] = useState(false);
  const { logout } = useAuth();

  // チャットアクションメニュー / 未読プレビュー
  // 担当者以外が誤って既読にしてしまうのを避けるため、リストから既読を付けずに
  // メッセージを覗ける手段を提供する。`thread` を触らないので個別スレッド WS は
  // 接続されず、markRead も呼ばれない。
  const [menuThreadId, setMenuThreadId] = useState(null);
  const [previewThread, setPreviewThread] = useState(null);
  const [previewMessages, setPreviewMessages] = useState([]);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);

  const sidebarRef = useRef(null);
  const chatAreaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const textAreaRef = useRef(null);

  const isInitialLoadRef = useRef(true);
  const oldScrollHeightRef = useRef(0);
  const loadingOlderRef = useRef(false);

  // .user-item の長押しで OS / ブラウザのネイティブ コンテキストメニュー
  // (iOS の callout, Chrome DevTools の「戻る/別名で保存」など) が出ないように
  // document レベルで contextmenu を捕まえて preventDefault する。React の
  // onContextMenu を li に貼るだけだと long-press 由来イベントが取りこぼされる
  // ケースがあるため、document に native listener を貼る方が確実。
  useEffect(() => {
    const handler = (e) => {
      if (e.target?.closest?.(".user-item")) {
        e.preventDefault();
      }
    };
    document.addEventListener("contextmenu", handler);
    return () => document.removeEventListener("contextmenu", handler);
  }, []);

  // チャット認証の回復に失敗したら (refresh も bridge 再発行もダメ = セッション
  // 失効など) 再ログイン導線を表示する。slashAppClient が発火する。
  useEffect(() => {
    const onAuthLost = () => setAuthLost(true);
    window.addEventListener(SLASHAPP_AUTH_LOST_EVENT, onAuthLost);
    return () => window.removeEventListener(SLASHAPP_AUTH_LOST_EVENT, onAuthLost);
  }, []);

  const handleRelogin = async () => {
    await logout(); // isLoggedIn と slashapp トークンをクリア
    window.location.href = "/login";
  };

  // ?thread=<id> を読んで初期選択する (旧 ?user= から名称変更)
  const [searchParams] = useSearchParams();
  const initialThreadId = searchParams.get("thread");

  useEffect(() => {
    if (!initialThreadId || threads.length === 0 || thread) return;
    const found = threads.find((t) => t.id === parseInt(initialThreadId, 10));
    if (found) handleSelectThread(found);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialThreadId, threads]);

  // --------------------- フィルタ / 検索 ---------------------
  const handleChangeFilter = (newFilter) => {
    setFilter(newFilter);
    setThreadPage(1);
    setHasMoreThreads(true);
  };
  const handleSearchInputChange = (e) => setSearchInput(e.target.value);
  const handleSearchKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setSearchTerm(searchInput);
      setThreadPage(1);
      setHasMoreThreads(true);
    }
  };

  // --------------------- スレッド一覧取得 ---------------------
  const fetchThreads = useCallback(
    async (page = 1) => {
      if (isLoadingThreads) return;
      try {
        setIsLoadingThreads(true);
        const data = await slashAppChat.listThreads({
          page,
          filter,
          q: searchTerm,
        });
        const rows = data.results || [];
        setHasMoreThreads(!!data.has_more);
        if (page === 1) {
          setThreads(rows);
        } else {
          setThreads((prev) => {
            const uniques = rows.filter(
              (t) => !prev.some((p) => p.id === t.id)
            );
            return [...prev, ...uniques];
          });
        }
      } catch (e) {
        console.error("threads fetch error:", e);
      } finally {
        setIsLoadingThreads(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filter, searchTerm]
  );

  useEffect(() => {
    fetchThreads(threadPage);
  }, [threadPage, filter, searchTerm, fetchThreads]);

  const handleScrollThreadList = (e) => {
    if (!hasMoreThreads || isLoadingThreads) return;
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 1) {
      setThreadPage((prev) => prev + 1);
    }
  };

  // --------------------- Inbox WebSocket (一覧更新) ---------------------
  const inboxRetryRef = useRef(0);
  const inboxWsRef = useRef(null);

  useEffect(() => {
    let stopped = false;
    const connect = async () => {
      // 空 token での接続を避け、必要なら裏でトークンを回復させてから繋ぐ
      const token = await ensureSlashAppTokens();
      if (stopped) return;
      if (!token) return; // 回復不可。auth-lost バナーで再ログインを促す
      if (
        inboxWsRef.current &&
        inboxWsRef.current.readyState === WebSocket.OPEN
      ) {
        inboxWsRef.current.close();
      }
      const ws = slashAppChat.openInboxSocket();
      inboxWsRef.current = ws;
      ws.onopen = () => {
        inboxRetryRef.current = 0;
      };
      ws.onerror = () => ws.close();
      ws.onclose = () => {
        if (stopped) return;
        const delay =
          Math.min(10000, 1000 * 2 ** inboxRetryRef.current) +
          Math.random() * 3000;
        inboxRetryRef.current += 1;
        setTimeout(connect, delay);
      };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === "thread.updated" && data.thread) {
            mergeUpdatedThread(data.thread);
          }
        } catch (_) {}
      };
    };
    connect();
    return () => {
      stopped = true;
      if (inboxWsRef.current) {
        inboxWsRef.current.close();
        inboxWsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mergeUpdatedThread = useCallback(
    (updated) => {
      setThreads((prev) => {
        const exists = prev.some((t) => t.id === updated.id);
        const next = exists
          ? prev.map((t) => (t.id === updated.id ? updated : t))
          : [updated, ...prev];
        next.sort(
          (a, b) =>
            new Date(b.last_message_at || b.created_at) -
            new Date(a.last_message_at || a.created_at)
        );
        return next;
      });
      // 現在開いているスレッドが対象なら status/is_read も追従
      if (thread && thread.id === updated.id) {
        setThread((prev) => ({
          ...prev,
          status: updated.status,
          is_read: updated.is_read,
        }));
      }
    },
    [thread]
  );

  // --------------------- 個別スレッド WebSocket ---------------------
  const threadWsRef = useRef(null);
  const threadWsRetryRef = useRef(0);

  useEffect(() => {
    if (!thread?.id) return;
    let stopped = false;
    const connect = async () => {
      const token = await ensureSlashAppTokens();
      if (stopped) return;
      if (!token) return; // 回復不可。auth-lost バナーで再ログインを促す
      if (
        threadWsRef.current &&
        threadWsRef.current.readyState === WebSocket.OPEN
      ) {
        threadWsRef.current.close();
      }
      const ws = slashAppChat.openThreadSocket(thread.id);
      threadWsRef.current = ws;
      ws.onopen = () => {
        threadWsRetryRef.current = 0;
      };
      ws.onerror = () => ws.close();
      ws.onclose = () => {
        if (stopped) return;
        const delay =
          Math.min(10000, 1000 * 2 ** threadWsRetryRef.current) +
          Math.random() * 3000;
        threadWsRetryRef.current += 1;
        setTimeout(connect, delay);
      };
      ws.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          if (data.type === "message.new" && data.message) {
            const m = data.message;
            setMessages((prev) =>
              prev.some((x) => x.id === m.id) ? prev : [...prev, m]
            );
            if (!isFromInstructor(m)) {
              slashAppChat.markRead(thread.id).catch(() => {});
              if (!shouldAutoScroll) {
                setIncomingMessage({
                  text: m.text || "",
                  userName: thread.customer?.full_name || "",
                });
              }
            }
          } else if (data.type === "message.unsent" && data.message) {
            const m = data.message;
            setMessages((prev) =>
              prev.map((x) => (x.id === m.id ? { ...x, deleted: true } : x))
            );
          } else if (data.type === "message.updated" && data.message) {
            // 今のところ LINE 配信ステータスの確定 (送信中 → 送信済 /
            // 送信失敗) のみ。既存行をサーバの内容で差し替える。
            const m = data.message;
            setMessages((prev) =>
              prev.map((x) => (x.id === m.id ? { ...x, ...m } : x))
            );
          }
        } catch (_) {}
      };
    };
    connect();
    return () => {
      stopped = true;
      if (threadWsRef.current) {
        threadWsRef.current.close();
        threadWsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread?.id]);

  // --------------------- メッセージ取得 ---------------------
  const loadInitialMessages = useCallback(async (threadId) => {
    if (!threadId) return;
    setIsLoadingMessages(true);
    try {
      const data = await slashAppChat.listMessages(threadId);
      const rows = (data.results || []).slice().reverse(); // 古い→新しい
      setMessages(rows);
      setHasMoreMessages(!!data.has_more);
      setOldestId(data.next_before_id);
    } catch (e) {
      console.error("messages fetch error:", e);
    } finally {
      setIsLoadingMessages(false);
    }
  }, []);

  const loadOlderMessages = useCallback(async () => {
    if (!thread?.id || !hasMoreMessages || isLoadingMessages || oldestId == null)
      return;
    setIsLoadingMessages(true);
    try {
      const data = await slashAppChat.listMessages(thread.id, {
        beforeId: oldestId,
      });
      const older = (data.results || []).slice().reverse();
      setMessages((prev) => {
        const uniques = older.filter((m) => !prev.some((x) => x.id === m.id));
        return [...uniques, ...prev];
      });
      setHasMoreMessages(!!data.has_more);
      setOldestId(data.next_before_id);
    } catch (e) {
      console.error("older messages fetch error:", e);
    } finally {
      setIsLoadingMessages(false);
    }
  }, [thread, hasMoreMessages, isLoadingMessages, oldestId]);

  // --------------------- スクロール ---------------------
  const handleScrollChatArea = (e) => {
    const { scrollTop, clientHeight, scrollHeight } = e.target;
    if (scrollTop <= 10 && hasMoreMessages && !loadingOlderRef.current) {
      oldScrollHeightRef.current = scrollHeight;
      loadingOlderRef.current = true;
      loadOlderMessages().finally(() => {
        loadingOlderRef.current = false;
      });
    }
    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setShouldAutoScroll(true);
      setIncomingMessage(null);
    } else {
      setShouldAutoScroll(false);
    }
  };

  useEffect(() => {
    if (thread?.id) {
      isInitialLoadRef.current = true;
      setIncomingMessage(null);
      setOldestId(null);
      setHasMoreMessages(true);
      loadInitialMessages(thread.id);
    } else {
      setMessages([]);
    }
  }, [thread?.id, loadInitialMessages]);

  useEffect(() => {
    const container = chatAreaRef.current;
    if (!container) return;
    if (isInitialLoadRef.current) {
      setTimeout(() => {
        scrollToBottom();
        isInitialLoadRef.current = false;
      }, 100);
      return;
    }
    if (shouldAutoScroll) scrollToBottom();
  }, [messages, shouldAutoScroll]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
  };

  // --------------------- スレッド選択 / 既読 ---------------------
  const handleSelectThread = async (selected) => {
    setSelectedFiles([]);
    setThread(selected);
    if (!selected.is_read) {
      try {
        await slashAppChat.markRead(selected.id);
        setThreads((prev) =>
          prev.map((t) =>
            t.id === selected.id ? { ...t, is_read: true } : t
          )
        );
      } catch (e) {
        console.error("markRead failed:", e);
      }
    }
  };

  // --------------------- チャットアクション / プレビュー ---------------------
  const closeMenu = () => setMenuThreadId(null);

  const handleItemClick = (t) => {
    // 長押し直後の click をスレッド選択 (= markRead) として扱わない
    if (longPressFiredRef.current) {
      longPressFiredRef.current = false;
      return;
    }
    if (menuThreadId === t.id) {
      // メニューが開いている状態でアイテム自体を踏んだら閉じるだけにする
      closeMenu();
      return;
    }
    handleSelectThread(t);
  };

  const handleKebabClick = (e, t) => {
    e.stopPropagation();
    setMenuThreadId((prev) => (prev === t.id ? null : t.id));
  };

  const handleTouchStart = (t) => {
    longPressFiredRef.current = false;
    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
    longPressTimerRef.current = setTimeout(() => {
      longPressFiredRef.current = true;
      setMenuThreadId(t.id);
    }, 500);
  };

  const cancelLongPress = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const openPreview = async (t) => {
    closeMenu();
    setPreviewThread(t);
    setPreviewMessages([]);
    setIsLoadingPreview(true);
    try {
      const data = await slashAppChat.listMessages(t.id);
      const rows = (data.results || []).slice().reverse(); // 古い→新しい
      setPreviewMessages(rows);
    } catch (e) {
      console.error("preview fetch error:", e);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const closePreview = () => {
    setPreviewThread(null);
    setPreviewMessages([]);
  };

  // スマホ表示で右ペインから戻るときに、スレッド非選択状態に戻して左ペインを
  // 表示させる (WS は useEffect の cleanup で自動切断)。
  const handleBackToList = () => {
    setThread(null);
    setMessages([]);
    setOldestId(null);
    setHasMoreMessages(true);
    setIncomingMessage(null);
    setSelectedFiles([]);
    setDraft("");
  };

  // --------------------- 送信 ---------------------
  const handleSend = async () => {
    if (!thread?.id) return;
    if (!draft.trim() && selectedFiles.length === 0) return;
    try {
      setIsSending(true);
      if (draft.trim()) {
        const msg = await slashAppChat.sendText(thread.id, draft);
        setMessages((prev) =>
          prev.some((x) => x.id === msg.id) ? prev : [...prev, msg]
        );
      }
      for (const file of selectedFiles) {
        const up = await slashAppChat.upload(thread.id, file);
        const msg = await slashAppChat.sendMedia(
          thread.id,
          up.message_type,
          up.s3_url
        );
        setMessages((prev) =>
          prev.some((x) => x.id === msg.id) ? prev : [...prev, msg]
        );
      }
      setDraft("");
      setSelectedFiles([]);
      if (textAreaRef.current) {
        textAreaRef.current.style.height = "auto";
        textAreaRef.current.style.overflowY = "hidden";
      }
      scrollToBottom();
    } catch (e) {
      console.error("send failed:", e);
      alert("送信に失敗しました: " + e.message);
    } finally {
      setIsSending(false);
    }
  };

  // --------------------- 送信取消 ---------------------
  const handleUnsend = async (messageId) => {
    try {
      await slashAppChat.unsend(messageId);
    } catch (e) {
      console.error("unsend failed:", e);
    }
  };

  // --------------------- LINE 再送 ---------------------
  const handleResendLine = async (messageId) => {
    if (resendingIds.includes(messageId)) return;
    setResendingIds((prev) => [...prev, messageId]);
    try {
      const updated = await slashAppChat.resendLine(messageId);
      // WebSocket でも同じ更新が飛んでくるが、切断中でも即反映されるように
      // レスポンスでも上書きしておく。
      setMessages((prev) =>
        prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x))
      );
      if (updated.line_delivery_status === "failed") {
        alert(
          "再送しましたが、まだ届いていません。\n" +
            (updated.line_delivery_error || "")
        );
      }
    } catch (e) {
      console.error("resend failed:", e);
      alert("再送に失敗しました: " + e.message);
    } finally {
      setResendingIds((prev) => prev.filter((id) => id !== messageId));
    }
  };

  const handleClickIncoming = () => {
    scrollToBottom();
    setIncomingMessage(null);
  };

  // --------------------- 要対応 / 対応済 ---------------------
  const handleChangeStatus = async (newStatus) => {
    if (!thread?.id) return;
    try {
      const updated = await slashAppChat.setStatus(thread.id, newStatus);
      setThread((prev) => ({ ...prev, status: updated.status }));
    } catch (e) {
      console.error("status update failed:", e);
    }
  };

  // --------------------- 日時フォーマット ---------------------
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const now = new Date();
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    const isToday =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
    const isSameYear = date.getFullYear() === now.getFullYear();
    const weekdayNames = ["日", "月", "火", "水", "木", "金", "土"];
    const weekday = weekdayNames[date.getDay()];
    const z = (n) => n.toString().padStart(2, "0");
    const hours = z(date.getHours());
    const minutes = z(date.getMinutes());
    if (isToday) return `${hours}:${minutes}`;
    if (isSameYear)
      return `${z(date.getMonth() + 1)}/${z(date.getDate())}(${weekday}) ${hours}:${minutes}`;
    return `${date.getFullYear()}/${z(date.getMonth() + 1)}/${z(date.getDate())}(${weekday}) ${hours}:${minutes}`;
  };

  // スレッド一覧 名前下に出す直近メッセージのプレビュー文字列。
  // 非テキスト系は LINE 風に種別ラベルを返す。`last_message` は slash-app 側で
  // deleted を除外しているのでここでは考慮不要。
  const formatThreadPreview = (t) => {
    const m = t?.last_message;
    if (!m) return "";
    switch (m.message_type) {
      case "text":
        return (m.text || "").replace(/\s+/g, " ");
      case "sticker":
        return "[スタンプ]";
      case "image":
        return "[画像]";
      case "video":
        return "[動画]";
      case "file":
        return m.file_name || "[ファイル]";
      default:
        return "";
    }
  };

  // スレッド一覧右上の最終やり取り時刻。今日は HH:MM、昨日以前は MM/DD。
  const formatThreadTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (Number.isNaN(date.getTime())) return "";
    const now = new Date();
    const isToday =
      date.getFullYear() === now.getFullYear() &&
      date.getMonth() === now.getMonth() &&
      date.getDate() === now.getDate();
    const z = (n) => n.toString().padStart(2, "0");
    if (isToday) return `${z(date.getHours())}:${z(date.getMinutes())}`;
    return `${z(date.getMonth() + 1)}/${z(date.getDate())}`;
  };

  const linkifyText = (text) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    const parts = (text || "").split(urlRegex);
    return parts.map((part, index) =>
      urlRegex.test(part) ? (
        <a key={index} href={part} target="_blank" rel="noopener noreferrer">
          {part}
        </a>
      ) : (
        part
      )
    );
  };

  // --------------------- textarea auto-grow ---------------------
  const handleTextAreaChange = (e) => {
    setDraft(e.target.value);
    const el = textAreaRef.current;
    if (!el) return;
    el.style.height = "auto";
    const max = window.innerHeight / 2.5;
    const sh = el.scrollHeight;
    if (sh > max) {
      el.style.height = `${max}px`;
      el.style.overflowY = "auto";
    } else {
      el.style.height = `${sh}px`;
      el.style.overflowY = "hidden";
    }
  };

  // --------------------- メディア拡大表示 ---------------------
  const handleOpenMedia = (type, url) => setExpandedMedia({ type, url });
  const handleCloseMedia = () => setExpandedMedia(null);

  // --------------------- ファイル選択 ---------------------
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0)
      setSelectedFiles((prev) => [...prev, ...files]);
    e.target.value = null;
  };
  const openFileDialog = () => fileInputRef.current?.click();
  const removeFile = (idx) =>
    setSelectedFiles((prev) => prev.filter((_, i) => i !== idx));

  // --------------------- ドラッグ&ドロップ ---------------------
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = Array.from(e.dataTransfer.files);
    const media = files.filter(
      (f) => f.type.startsWith("image/") || f.type.startsWith("video/")
    );
    if (media.length > 0) setSelectedFiles((prev) => [...prev, ...media]);
  };

  const renderFilePreview = (file, index) => {
    const isImage = file.type.startsWith("image/");
    const isVideo = file.type.startsWith("video/");
    const fileURL = URL.createObjectURL(file);
    return (
      <div key={index} className="file-preview-item">
        {isImage && (
          <img src={fileURL} alt={file.name} className="file-preview-image" />
        )}
        {isVideo && <Video className="file-preview-image" />}
        <button className="file-remove-btn" onClick={() => removeFile(index)}>
          <X size={16} />
        </button>
      </div>
    );
  };

  // --------------------- レンダリング ---------------------
  const customer = thread?.customer || {};
  return (
    <section
      className={`chat-container ${thread?.id ? "has-active-thread" : ""}`}
    >
      {authLost && (
        <div className="chat-auth-lost-banner">
          <span>
            チャットの認証情報が取得できませんでした。再ログインしてください。
          </span>
          <button type="button" onClick={handleRelogin}>
            再ログイン
          </button>
        </div>
      )}
      {/* サイドバー — 実際にスクロールするのはこの要素なので、無限スクロールの
          onScroll はここに付ける。 */}
      <div
        className="sidebar-container"
        ref={sidebarRef}
        onScroll={handleScrollThreadList}
      >
        <div className="sidebar-header">
          <select
            value={filter}
            onChange={(e) => handleChangeFilter(e.target.value)}
          >
            <option value="">すべて</option>
            <option value="receiving">受信</option>
            <option value="unread">未読</option>
            <option value="required">要対応</option>
            <option value="completed">対応済</option>
          </select>
          <div className="search-wrapper">
            <input
              type="text"
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyDown={handleSearchKeyDown}
              placeholder="検索ワード (Enterで検索)"
            />
          </div>
        </div>

        <div className="user-list-scroll">
          <ul className="user-list">
            {threads.map((t) => {
              const c = t.customer || {};
              const name = c.full_name || c.auth_username || "(名前未設定)";
              const line = isLineThread(t);
              const menuOpen = menuThreadId === t.id;
              return (
                <li
                  key={t.id}
                  className={`user-item ${thread?.id === t.id ? "active" : ""} ${menuOpen ? "menu-open" : ""}`}
                  onClick={() => handleItemClick(t)}
                  onTouchStart={() => handleTouchStart(t)}
                  onTouchEnd={cancelLongPress}
                  onTouchMove={cancelLongPress}
                  onTouchCancel={cancelLongPress}
                  onContextMenu={(e) => e.preventDefault()}
                >
                  {line ? (
                    <LineAvatar src={c.line_picture_url} alt={name} />
                  ) : (
                    <div className="user-avatar">{name.charAt(0) || "?"}</div>
                  )}
                  <div className="user-info">
                    <div className="user-name">{name}</div>
                    {!line && <FamilyNote customer={c} />}
                    {t.last_message && (
                      <div className="user-preview">
                        {formatThreadPreview(t)}
                      </div>
                    )}
                    <div className="user-source">
                      <SourceBadge thread={t} />
                      {t.status !== null && t.status !== undefined && (
                        <span
                          className={`user-status-badge ${
                            t.status === 1 ? "required" : "completed"
                          }`}
                        >
                          {t.status === 1 ? "要対応" : "対応済"}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="user-trailing">
                    {!t.is_read && <span className="unread">●</span>}
                    <button
                      type="button"
                      className="thread-kebab"
                      aria-label="チャットアクション"
                      onClick={(e) => handleKebabClick(e, t)}
                    >
                      <MoreVertical size={18} />
                    </button>
                  </div>
                  {(t.last_message_at || t.created_at) && (
                    <span className="user-time">
                      {formatThreadTime(t.last_message_at || t.created_at)}
                    </span>
                  )}
                  {menuOpen && (
                    <div
                      className="thread-menu"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        className="thread-menu-item"
                        onClick={() => openPreview(t)}
                      >
                        受信メッセージのプレビュー
                      </button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {isLoadingThreads && <ScrollLoader />}
        </div>
      </div>

      {/* チャットエリア */}
      <div
        className={`chat-area ${isDragging ? "dragging" : ""}`}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {thread?.id ? (
          <>
            <div className="chat-header">
              <button
                type="button"
                className="back-to-list"
                onClick={handleBackToList}
                aria-label="スレッド一覧に戻る"
              >
                <ChevronLeft size={22} />
              </button>
              <p>
                <SourceBadge thread={thread} />
                {customer.full_name || customer.auth_username || ""}
                {!isLineThread(thread) && customer.studio && (
                  <span>（{customer.studio}）</span>
                )}
                {!isLineThread(thread) && <FamilyNote customer={customer} />}
              </p>
              <div className="status-buttons">
                <button
                  className={`status-btn required ${
                    thread.status === 1 ? "active" : ""
                  }`}
                  onClick={() => handleChangeStatus(1)}
                >
                  要対応
                </button>
                <button
                  className={`status-btn completed ${
                    thread.status === 0 ? "active" : ""
                  }`}
                  onClick={() => handleChangeStatus(0)}
                >
                  対応済
                </button>
              </div>
            </div>

            <div
              className="chat-messages"
              ref={chatAreaRef}
              onScroll={handleScrollChatArea}
            >
              {isSending && (
                <div className="loading-overlay">
                  <Loader className="loading-spinner" />
                </div>
              )}
              {isLoadingMessages && <ScrollLoader />}

              <ul className="message-list">
                {messages.map((m) => {
                  const mine = isFromInstructor(m);
                  return (
                    <li
                      key={m.id}
                      className={`message-item ${mine ? "admin" : "user"}`}
                    >
                      {m.message_type === "text" && (
                        <div
                          className={`message-bubble ${m.deleted ? "deleted" : ""}`}
                        >
                          {linkifyText(m.text || "")}
                        </div>
                      )}

                      {m.message_type === "sticker" && (
                        <div
                          className={`message-bubble sticker-bubble ${m.deleted ? "deleted" : ""}`}
                        >
                          {m.text || "[スタンプ]"}
                        </div>
                      )}

                      {m.message_type === "image" && !m.deleted && (
                        <div className="message-bubble image-bubble">
                          <img
                            src={m.s3_url}
                            alt=""
                            onClick={() => handleOpenMedia("image", m.s3_url)}
                          />
                        </div>
                      )}

                      {m.message_type === "video" && !m.deleted && (
                        <div className="message-bubble video-bubble">
                          <video
                            src={m.s3_url}
                            width="200"
                            onClick={() => handleOpenMedia("video", m.s3_url)}
                          />
                        </div>
                      )}

                      {m.message_type === "file" && !m.deleted && (
                        <div className="message-bubble file-bubble">
                          <a
                            href={m.s3_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={m.file_name || undefined}
                          >
                            <Paperclip size={18} />
                            <span className="file-name">
                              {m.file_name || "ファイル"}
                            </span>
                          </a>
                        </div>
                      )}

                      {m.deleted &&
                        m.message_type !== "text" &&
                        m.message_type !== "sticker" && (
                          <div className="message-bubble deleted">
                            削除されたメッセージです
                          </div>
                        )}

                      <div className="message-time">
                        {formatDate(m.received_at)}
                      </div>

                      {mine && !m.deleted && (
                        <LineDeliveryNotice
                          message={m}
                          onResend={handleResendLine}
                          resending={resendingIds.includes(m.id)}
                        />
                      )}

                      {m.deleted ? (
                        <div
                          className={`message-deleted ${mine ? "admin" : "user"}`}
                        >
                          メッセージの送信を取消しました
                        </div>
                      ) : (
                        // LINE 経由スレッドは LINE 側のメッセージを取り消せない
                        // (Messaging API に送信済みメッセージの削除がない) ため、
                        // 送信取消ボタン自体を出さない。
                        mine &&
                        !isLineThread(thread) && (
                          <div className="message-actions">
                            <button
                              className="unsend-button"
                              onClick={() => handleUnsend(m.id)}
                            >
                              送信取消
                            </button>
                          </div>
                        )
                      )}
                    </li>
                  );
                })}
                <div ref={messagesEndRef} />
              </ul>
            </div>

            {incomingMessage && (
              <div className="incoming-notice" onClick={handleClickIncoming}>
                <span className="arrow">↓</span>
                <strong>新着メッセージ：</strong>
                {incomingMessage.text}
              </div>
            )}

            {selectedFiles.length > 0 && (
              <div className="file-preview-container">
                {selectedFiles.map((file, idx) => renderFilePreview(file, idx))}
              </div>
            )}

            <div className="chat-input">
              <textarea
                ref={textAreaRef}
                className="chat-textarea"
                value={draft}
                onChange={handleTextAreaChange}
                placeholder="メッセージを入力..."
                rows={3}
              />
              <div className="chat-controls">
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleFileSelect}
                  accept="image/*,video/*"
                  multiple
                />
                <button
                  className="media-upload-button"
                  onClick={openFileDialog}
                >
                  <Image size={20} />
                </button>
                <button
                  className="send-button"
                  onClick={handleSend}
                  disabled={!draft.trim() && selectedFiles.length === 0}
                >
                  <Send size={20} />
                </button>
              </div>
              {isLineThread(thread) && (
                <div className="chat-input-note">
                  ※ LINE 経由の問い合わせには送信取消はできません
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">💬</div>
            <div className="empty-state-text">
              左側のユーザーリストからチャットを選択してください
            </div>
          </div>
        )}
      </div>

      {menuThreadId !== null && (
        <div className="thread-menu-overlay" onClick={closeMenu} />
      )}

      {previewThread && (
        <div className="preview-modal-overlay" onClick={closePreview}>
          <div
            className="preview-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="preview-modal-header">
              <span className="preview-modal-title">受信メッセージのプレビュー</span>
              <button
                type="button"
                className="preview-modal-close"
                onClick={closePreview}
                aria-label="閉じる"
              >
                <X size={22} />
              </button>
            </div>
            <div className="preview-modal-note">
              このプレビューでは、メッセージを既読にせずに、未読バッジを残したまま、受信メッセージを確認できます。
            </div>
            <div className="preview-modal-userline">
              {isLineThread(previewThread) ? (
                <LineAvatar
                  src={previewThread.customer?.line_picture_url}
                  alt={previewThread.customer?.full_name || ""}
                />
              ) : (
                <div className="user-avatar">
                  {(
                    previewThread.customer?.full_name ||
                    previewThread.customer?.auth_username ||
                    "?"
                  ).charAt(0)}
                </div>
              )}
              <span className="preview-modal-username">
                {previewThread.customer?.full_name ||
                  previewThread.customer?.auth_username ||
                  "(名前未設定)"}
                {!isLineThread(previewThread) && (
                  <FamilyNote customer={previewThread.customer} />
                )}
              </span>
            </div>
            <div className="preview-modal-body">
              {isLoadingPreview && <ScrollLoader />}
              <ul className="message-list">
                {previewMessages.map((m) => {
                  const mine = isFromInstructor(m);
                  return (
                    <li
                      key={m.id}
                      className={`message-item ${mine ? "admin" : "user"}`}
                    >
                      {m.message_type === "text" && (
                        <div
                          className={`message-bubble ${m.deleted ? "deleted" : ""}`}
                        >
                          {linkifyText(m.text || "")}
                        </div>
                      )}
                      {m.message_type === "sticker" && (
                        <div
                          className={`message-bubble sticker-bubble ${m.deleted ? "deleted" : ""}`}
                        >
                          {m.text || "[スタンプ]"}
                        </div>
                      )}
                      {m.message_type === "image" && !m.deleted && (
                        <div className="message-bubble image-bubble">
                          <img src={m.s3_url} alt="" />
                        </div>
                      )}
                      {m.message_type === "video" && !m.deleted && (
                        <div className="message-bubble video-bubble">
                          <video src={m.s3_url} width="200" />
                        </div>
                      )}
                      {m.message_type === "file" && !m.deleted && (
                        <div className="message-bubble file-bubble">
                          <span>
                            <Paperclip size={18} />
                            <span className="file-name">
                              {m.file_name || "ファイル"}
                            </span>
                          </span>
                        </div>
                      )}
                      {m.deleted &&
                        m.message_type !== "text" &&
                        m.message_type !== "sticker" && (
                          <div className="message-bubble deleted">
                            削除されたメッセージです
                          </div>
                        )}
                      <div className="message-time">
                        {formatDate(m.received_at)}
                      </div>
                      {mine && !m.deleted && <LineDeliveryNotice message={m} />}
                    </li>
                  );
                })}
                {!isLoadingPreview && previewMessages.length === 0 && (
                  <div className="preview-modal-empty">
                    メッセージがありません
                  </div>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {expandedMedia && (
        <div className="media-modal-overlay" onClick={handleCloseMedia}>
          <div
            className="media-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <button className="media-modal-close" onClick={handleCloseMedia}>
              ×
            </button>
            {expandedMedia.type === "image" ? (
              <img src={expandedMedia.url} alt="" className="media-modal-img" />
            ) : (
              <video
                src={expandedMedia.url}
                controls
                className="media-modal-video"
              />
            )}
            <a
              href={expandedMedia.url}
              download
              className="media-modal-download"
              target="_blank"
              rel="noopener noreferrer"
            >
              ダウンロード
            </a>
          </div>
        </div>
      )}
    </section>
  );
}

export default ChatPage;
